
import { connect } from "react-redux";
import PropTypes from "prop-types";
import React, { Component, useState } from "react";
import { withTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  Alert,
  Card,
  CardBody,
  Col,
  Spinner,
  Label,
  Row,
  ListGroup,
  ListGroupItem,
  Container
} from "reactstrap";
import { Form, Formik, Field, ErrorMessage } from "formik";
import RavenFormikField from "components/Common/RavenFormikField";

import { displayNoneInline, getInitValue, uniqueIdentifier, getInvalid } from "components/Common/TableComponents/AgTable/CellRenderers/CellFunctions";
import { getInstallments, installmentApiError, initializePayData, merchantLinkDoPayment, merchantLinkCheckBinCardNumber, clearMerchantLinkCheckBinError, getMerchantPermissionByMerchantId } from "store/actions";
import i18n from "../../../i18n";
import { subscribeFormikLanguageRevalidation } from "../../../helpers/formikLanguageRevalidation";
import { formatCheckoutCurrency } from "../../../helpers/checkoutDisplay";
import { getApiErrorMessage } from "../../../helpers/apiErrorHelper";

import morLink from "../../../assets/images/icons/link-payment-icon.svg";
import tektik from "../../../assets/images/icons/footer-bank-1.svg";
import morPosKorumaliAlisveris from "../../../assets/images/icons/footer-bank-2.svg";
import footerBank from "../../../assets/images/footer-bank.png";
import morPosLogo from "../../../assets/images/MorPosLogo.png";
import styled from 'styled-components';
import LoginHeader from '../../../pages/Authentication/components/header';
import LoginFooter from '../../../pages/Authentication/components/footer';

const AccordionContainer = styled.div`
  border: 1px solid #00cc99;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const Title = styled.h4`
  margin: 0;
`;

const Icons = styled.div`
  display: flex;
  gap: 10px;

  img {
    height: 20px;
  }
`;

const ContentPersonalInfo = styled.div`
  margin-top: 20px;
  display: ${({ expandedPersonalInfo }) => (expandedPersonalInfo ? 'block' : 'none')};
  transition: all 0.3s ease;
`;

const ContentAddressInfo = styled.div`
  margin-top: 20px;
  display: ${({ expandedAddressInfo }) => (expandedAddressInfo ? 'block' : 'none')};
  transition: all 0.3s ease;
`;

const ContentCardInfo = styled.div`
  margin-top: 20px;
  display: ${({ expandedCardInfo }) => (expandedCardInfo ? 'block' : 'none')};
  transition: all 0.3s ease;
`;

const phoneRegex = /[1-9]{1}[0-9]{9}/;
const startsWithSpecialChars = new RegExp(/^'?\p{L}+(?:[' ]\p{L}+)*'?$/u)
const cardHolderLettersPattern = /^[\p{L}\s]+$/u;
const objectNames = Object.freeze({
  cardHolder: 'cardHolder',
  cardNumber: 'cardNumber',
  expireDate: 'expireDate',
  cvv: 'cvv',
  installmentCount: 'installmentCount',
  merchantInfo: 'merchantInfo',
  threeDPay: 'threeDPay',
  linkKey: 'linkKey',
  installmentAmount: 'installmentAmount'
});

const EXPIRY_MAX_DIGITS = 4
const getExpiryDigits = value =>
  (value ?? "").toString().replace(/\D/g, "").slice(0, EXPIRY_MAX_DIGITS)

const formatExpiryForDisplay = value => {
  const digits = getExpiryDigits(value)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`
}
const visaSrc = "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";
const masterSrc = "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";
const troySrc = "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";
let showCardBrand = false;
let cardBrandSrc = "";
let permissionMerchantId = "0";

class CardPaymentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      verticalActiveTab: "1",
      verticalActiveTabWithIcon: "1",
      col5: false,
      expandedPersonalInfo: false,
      expandedAddressInfo: false,
      expandedCardInfo: false,
      kvkkConsent: false,
      showKvkkModal: false,
      privacyConsent: false,
      showPrivacyModal: false,
      installmentDontShow: true,
      cardNumberValueCheck: false,
      threeDPay: false,
      installmentCount: "1",
      installmentAmount: "0.00",
      merchantInfo: {},
      installmentList: [
        {
          "installmentId": "1",
          "installmentRate": 0,
          "installmentAmountSimulation": this.props.recordList?.u,
          "currency": "949",
          "installmentTotalAmount": this.props.recordList?.u
        }
      ],
      merchantPermissionModal: false,
      i18nLanguage: i18n.language,
    };
    this.formikRef = React.createRef();
    this.lastQueriedBin = null;
    localStorage.setItem("I18N_LANGUAGE", "tr");
  }

  componentDidMount() {
    this.unsubscribeFormikLanguage = subscribeFormikLanguageRevalidation(this, this.formikRef);
  }

  componentWillUnmount() {
    this.unsubscribeFormikLanguage?.();
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { error, recordMerchantPermission, onGetMerchantPermissionByMerchantId, recordList, onInitializePayData, payData } = this.props;

    if (prevProps.error !== error) {
      this.setState({ error: error });
      this.setState({ errorModal: true });
    }

    if (this.props.recordList?.t && permissionMerchantId == "0") {
      permissionMerchantId = this.props.recordList?.t;
      onGetMerchantPermissionByMerchantId({ merchantId: permissionMerchantId });
    }

    if (prevProps.recordMerchantPermission !== recordMerchantPermission) {
      this.setState({ recordMerchantPermission: recordMerchantPermission });
      if (recordMerchantPermission.linkPaymentEnabled == false) {
        this.setState({ merchantPermissionModal: true });
      }
    }

    const shouldBeCheckedAndDisabled =
      this.props.recordMerchantPermission.threeDPaymentEnabled &&
      !this.props.recordMerchantPermission.noneSecurePaymentEnabled;

    if (shouldBeCheckedAndDisabled && !this.state.threeDPay) {
      this.setState({ threeDPay: true }, () => {
        this.props.onInitializePayData(this.props.payData, this.state.threeDPay, objectNames.threeDPay);
      });
    }
  }

  handleCardNoChange = (e) => {
    const digits = e.target.value.toString().replace(/\D/g, "");
    this.props.onInitializePayData(this.props.payData, digits, objectNames.cardNumber);
    this.props.onClearCheckBinError();

    if (digits.length < 6 || !this.isValidCardNumber(digits)) {
      this.lastQueriedBin = null;
      this.setState({ cardNumberValueCheck: false });
      showCardBrand = false;
      return;
    }

    const bin = digits.substring(0, 6);
    this.setState({ cardNumberValueCheck: true });

    if (bin === this.lastQueriedBin) {
      return;
    }
    this.lastQueriedBin = bin;

    this.checkBinCardNumber(bin);
    if (this.props.recordList?.n && this.props.recordList?.y === "TL") {
      this.props.onGetInstallments({
        bin,
        language: "tr",
        merchantId: this.props.recordList?.t,
        amount: this.props.recordList?.u,
        merchantGuid: this.props.recordList?.r,
      });
    }
  }

  isValidCardNumber = (cardNumber) => {
    const regex = /^\d+$/;
    return regex.test(cardNumber);
  }

  checkBinCardNumber(bin) {
    this.props.onGetCheckBinCardNumber({
      bin: bin.substring(0, 6),
      merchantId: this.props.recordList?.t,
      language: "tr",
    });
  }

  formOnSubmitMiddleware = values => {
    this.props.onInitializePayData(this.props.payData, values, objectNames.merchantInfo);
  };

  getParameterByName = () => {
    let url = window.location.href;
    const pathParts = url.split('/');
    const item = pathParts[pathParts.length - 1];
    return item;
  }

  payFormOnSubmitMiddleware = values => {
    this.props.onInitializePayData(this.props.payData, this.getParameterByName(), objectNames.linkKey);
    this.props.onPay(this.props.payData);

  };


  handleThreeDPay = (e) => {
    this.setState({ threeDPay: e.target.checked });
  }

  handleChangeOperation = (e, p, w, element) => {
    if (w === "c") {
      this.props.onInitializePayData(this.props.payData, e.target.id, p);
    }
    if (w === "s") {
      this.props.onInitializePayData(this.props.payData, e.target.value, p);
    }
    if (w === "ch") {
      this.props.onInitializePayData(this.props.payData, e.target.checked, p);
    }

    if (p === "installmentCount") {
      this.setState({ installmentCount: e.target.id });
      let installment = element.find(p => p.installmentId === e.target.id);
      this.props.onInitializePayData(this.props.payData, installment.installmentTotalAmount, objectNames.installmentAmount);
    }
    if (p === "threeDPay") {
      this.setState({ threeDPay: e.target.checked });
    }
  }

  toggleKvkkModal = () => {
    this.setState((prevState) => ({ showKvkkModal: !prevState.showKvkkModal }));
  };

  openModal = () => {
    this.setState({ showPrivacyModal: true });
  };

  closeModal = () => {
    this.setState({ showPrivacyModal: false });
  };

  acceptPrivacy = () => {
    this.setState({ privacyConsent: true, showPrivacyModal: false });
  };

  resolveCardBrandSrc = () => {
    const { checkBinCardRecord } = this.props;
    if (!checkBinCardRecord || checkBinCardRecord.responseCode !== "B0000") {
      return "";
    }
    const brand = checkBinCardRecord.cardBrand || "";
    if (brand.includes("VISA") || brand.includes("Visa")) {
      return visaSrc;
    }
    if (brand.includes("MASTERCARD") || brand.includes("MasterCard") || brand.includes("Master")) {
      return masterSrc;
    }
    if (brand.includes("TROY") || brand.includes("Troy")) {
      return troySrc;
    }
    return "";
  };

  render() {
    const formDefaults = {};
    const checkBinErrorMessage = getApiErrorMessage(this.props.checkBinError);
    const installmentErrorMessage = getApiErrorMessage(this.props.installmentError);
    const installmentList = Array.isArray(this.props.installmentRecord?.installmentList)
      ? this.props.installmentRecord.installmentList
      : null;
    const cardBrandSrc = this.state.cardNumberValueCheck && !this.props.checkBinLoading
      ? this.resolveCardBrandSrc()
      : "";

    const { expandedPersonalInfo, expandedAddressInfo, expandedCardInfo, merchantPermissionModal } = this.state;

    return (
      <React.Fragment>
        <Container>
          <Row className="justify-content-center" >

            <Card className="checkout-payment-card" style={{ border: '2px solid rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
              <CardBody className="pt-1">
                <Formik
                  innerRef={this.formikRef}
                  enableReinitialize={false}
                  initialValues={{
                    cardHolder: getInitValue(formDefaults, formDefaults.cardHolder),
                    cardNumber: getInitValue(formDefaults, formDefaults.cardNumber),
                    expireDate: formatExpiryForDisplay(getInitValue(formDefaults, formDefaults.expireDate)),
                    cvv: getInitValue(formDefaults, formDefaults.cvv),
                    nameSurname: getInitValue(formDefaults, formDefaults.nameSurname),
                    phoneNumber: getInitValue(formDefaults, formDefaults.phoneNumber),
                    email: getInitValue(formDefaults, formDefaults.email),
                    address: getInitValue(formDefaults, formDefaults.address),
                    conditionalAdress: this.props.recordList?.addressVisibled,
                  }}
                  validationSchema={Yup.object().shape({
                    cardHolder: Yup.string()
                      .matches(cardHolderLettersPattern,
                        this.props.t("latinletters"))
                      .matches(
                        /^(?:(?!\.).)*$\r?\n?/,
                        this.props.t("nosymbol"))
                      .max(50, this.props.t("maxLengthError").format("50"))
                      .min(6, this.props.t("minLengthError").format("6"))
                      .required(this.props.t("mustBe")),
                    cardNumber: Yup.string().matches(/\d/, this.props.t("onlyDigit"))
                      .max(16, this.props.t("maxLengthError").format("16"))
                      .matches(/^\S+$/, this.props.t("spaceNot"))
                      .required(this.props.t("mustBe")),
                    expireDate: Yup.string()
                      .transform(value => getExpiryDigits(value))
                      .matches(/^\d+$/, this.props.t("onlyDigit"))
                      .max(4, this.props.t("maxLengthError").format("4"))
                      .min(4, this.props.t("minLengthError").format("4"))
                      .required(this.props.t("mustBe")),
                    cvv: Yup.string().matches(/\d/, this.props.t("onlyDigit"))
                      .max(3, this.props.t("maxLengthError").format("3"))
                      .min(3, this.props.t("minLengthError").format("3"))
                      .matches(/^\S+$/, this.props.t("spaceNot"))
                      .required(this.props.t("mustBe")),
                    nameSurname: Yup.string()
                      .strict(true)
                      .when([], {
                        is: () => this.props.recordList?.f,
                        then: () => Yup.string()
                          .required(this.props.t("mustBe"))
                          .matches(/^[^.]*$/, this.props.t("noperiod"))
                          .matches(startsWithSpecialChars, this.props.t("nosymbol"))
                          .max(50, this.props.t("maxLengthError").format("50")),
                        otherwise: () => Yup.string().notRequired(),
                      }),
                    phoneNumber: Yup.string()
                      .strict(true)
                      .when([], {
                        is: () => this.props.recordList?.f,
                        then: () => Yup.string()
                          .strict(true)
                          .required(this.props.t("mustBe"))
                          .max(10, this.props.t("maxLengthError").format("10"))
                          .matches(phoneRegex, this.props.t("nosymbol")),
                        otherwise: () => Yup.string().notRequired(),
                      }),
                    email: Yup.string()
                      .strict(true)
                      .when([], {
                        is: () => this.props.recordList?.f,
                        then: () => Yup.string()
                          .strict(true)
                          .max(150, this.props.t("maxLengthError").format("150"))
                          .required(this.props.t("mustBe"))
                          .matches(
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/,
                            this.props.t("invalidEmail")
                          ),
                        otherwise: () => Yup.string().notRequired(),
                      }),
                    conditionalAdress: Yup.boolean(),
                    address: Yup.string()
                      .strict(true)
                      .when("conditionalAdress", {
                        is: (val) => val === false,
                        then: () =>
                          Yup.string()
                            .required(this.props.t("mustBe"))
                            .max(200, this.props.t("maxLengthError").format("100")),
                        otherwise: () => Yup.string().notRequired(),
                      }),
                  })}
                  onSubmit={values => {
                    this.payFormOnSubmitMiddleware(values);
                  }}
                >
                  {({ errors, touched, setFieldValue, values }) => (
                    <Form>
                      <fieldset disabled={false}>
                        <div style={{ textAlign: 'left', width: '100%' }}><LoginHeader /></div>
                        <Row style={{ marginTop: "10px" }}>

                          <Col xs={12} md={5}>

                            <div style={{ display: "flex", alignItems: "center", marginTop: "10px", marginBottom: "20px" }}>
                              <img
                                src={morLink}
                                alt="morLink"
                                style={{ width: '20px', height: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', marginRight: '10px' }}
                              />
                              < Label
                                className="form-label"
                                style={{
                                  marginTop: "10px",
                                  fontSize: "12px",
                                  textAlign: 'left',
                                  display: 'flex',
                                  justifyContent: 'left'
                                }}
                              >
                                {this.props.t("morparaLinkPayment")}
                              </Label>
                            </div>

                            <Card className="overflow-hidden" style={{ marginTop: '5px' }}>
                              <div
                                style={{
                                  border: '0.5px solid rgba(0, 0, 0, 0.08)',
                                  borderRadius: '8px',
                                  overflow: 'hidden'
                                }}
                              >
                                {/* Flex container */}
                                <div
                                  className="p-2"
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'nowrap',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    gap: '15px',
                                    minWidth: 0
                                  }}
                                >
                                  {/* Resim */}
                                  <div style={{ flexShrink: 0 }}>
                                    <img
                                      src={`data:image/png;base64,${this.props.recordList?.l}`}
                                      alt="Base64 Image"
                                      style={{
                                        width: '200px',
                                        height: '150px',
                                        objectFit: 'contain'
                                      }}
                                    />
                                  </div>

                                  {/* Ürün adi ve tutar */}
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      minWidth: 0,
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {/* Ürün adi */}
                                    <Label
                                      className="form-label"
                                      style={{
                                        fontSize: '15px',
                                        marginBottom: '10px',
                                        color: 'black',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%'
                                      }}
                                    >
                                      {this.props.recordList?.c}
                                    </Label>

                                    {/* Tutar */}
                                    <Label
                                      className="form-label"
                                      style={{
                                        fontSize: '25px',
                                        color: 'black',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%'
                                      }}
                                    >
                                      <strong className="checkout-currency-amount" dir="ltr">
                                        {formatCheckoutCurrency(
                                          this.state.installmentCount === "1"
                                            ? this.props.recordList?.u
                                            : this.props.payData?.amount,
                                          this.props.recordList?.y,
                                        )}
                                      </strong>
                                    </Label>
                                  </div>
                                </div>
                              </div>
                            </Card>


                            <div style={{ display: "flex", alignItems: "center", marginTop: "10px", marginBottom: "10px" }}>
                              <img
                                src={tektik}
                                alt="tektik"
                                style={{ width: '20px', height: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', marginRight: '10px' }}
                              />
                              < Label
                                className="form-label"
                                style={{
                                  marginTop: "10px",
                                  fontSize: "12px",
                                  textAlign: 'left',
                                  display: 'flex',
                                  justifyContent: 'left'
                                }}
                              >
                                {this.props.t("linkPaymentTekTik")}
                              </Label>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", marginTop: "10px", marginBottom: "10px" }}>
                              <img
                                src={morPosKorumaliAlisveris}
                                alt="morPosKorumaliAlisveris"
                                style={{ width: '20px', height: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', marginRight: '10px' }}
                              />
                              < Label
                                className="form-label"
                                style={{
                                  marginTop: "10px",
                                  fontSize: "12px",
                                  textAlign: 'left',
                                  display: 'flex',
                                  justifyContent: 'left'
                                }}
                              >
                                {this.props.t("morPosKorumaliAlisveris")}
                              </Label>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", marginTop: "10px", marginBottom: "10px" }}>
                              <img
                                src={footerBank}
                                alt="footerBank"
                                style={{ width: '225px', height: '25px', textAlign: 'center', display: 'flex', justifyContent: 'center', marginRight: '10px' }}
                              />
                            </div>

                          </Col>

                          <div className="w-100 d-md-none" />

                          <Col xs={12} md={6}>
                            <AccordionContainer style={{ display: this.props.recordList?.f ? 'block' : 'none' }}>
                              <Header onClick={() => this.setState({ expandedPersonalInfo: !expandedPersonalInfo })}>
                                <Title>{this.props.t("personalInfo")}</Title>
                              </Header>

                              <ContentPersonalInfo expandedPersonalInfo={expandedPersonalInfo}>
                                <Card>
                                  <CardBody>
                                    <Row>
                                      <Col lg={12} style={{ display: displayNoneInline(this.props.recordList?.nameSurnameVisibled) }}>
                                        <RavenFormikField
                                          errors={errors}
                                          touched={touched}
                                          name={"nameSurname"}
                                          label={"nameSurname"}
                                          type="text"
                                          placeholder={this.props.t("nameSurname")}
                                          hideLabel={true}
                                        />
                                      </Col>
                                      <Col lg={12} style={{ display: displayNoneInline(this.props.recordList?.mailVisibled) }}>
                                        <RavenFormikField
                                          errors={errors}
                                          touched={touched}
                                          name={"email"}
                                          label={"email"}
                                          type="text"
                                          placeholder={this.props.t("email")}
                                          hideLabel={true}
                                        />
                                      </Col>
                                      <Col lg={12} style={{ display: displayNoneInline(this.props.recordList?.phoneVisibled) }}>
                                        <RavenFormikField
                                          errors={errors}
                                          touched={touched}
                                          name={"phoneNumber"}
                                          label={"phoneNumber"}
                                          type="text"
                                          placeholder={this.props.t("phoneNumber")}
                                          hideLabel={true}
                                        />
                                      </Col>
                                    </Row>
                                  </CardBody>
                                </Card>
                              </ContentPersonalInfo>
                            </AccordionContainer>

                            <AccordionContainer style={{ display: this.props.recordList?.e ? 'block' : 'none' }}>
                              <Header onClick={() => this.setState({ expandedAddressInfo: !expandedAddressInfo })}>
                                <Title>{this.props.t("addressInfo")}</Title>
                              </Header>

                              <ContentAddressInfo expandedAddressInfo={expandedAddressInfo}>
                                <Card>
                                  <CardBody>
                                    <Row>
                                      <Col lg={12} style={{ display: displayNoneInline(this.props.recordList?.addressVisibled) }}>
                                        <RavenFormikField
                                          errors={errors}
                                          touched={touched}
                                          name={"address"}
                                          label={"address"}
                                          type="textarea"
                                          rows={3}
                                          placeholder={this.props.t("address")}
                                          hideLabel={true}
                                        />
                                      </Col>
                                    </Row>
                                  </CardBody>
                                </Card>
                              </ContentAddressInfo>
                            </AccordionContainer>

                            <AccordionContainer>

                              <Header onClick={() => this.setState({ expandedCardInfo: !expandedCardInfo })}>
                                <Title>{this.props.t("cardInfoPerso")}</Title>
                                <Icons>
                                  <img src={footerBank} alt="footerBank" />
                                </Icons>
                              </Header>

                              <ContentCardInfo expandedCardInfo={expandedCardInfo}>

                                <RavenFormikField
                                  errors={errors}
                                  touched={touched}
                                  name={"cardHolder"}
                                  label={"cardHolder"}
                                  type="text"
                                  maxLength={50}
                                  style={{ width: '90%' }}
                                  placeholder={this.props.t("cardHolder")}
                                  hideLabel={true}
                                  setFieldValue={setFieldValue}
                                  onChange={e => {
                                    this.handleChangeOperation(e, objectNames.cardHolder, "s");
                                  }}
                                />

                                <div style={{ position: 'relative', width: '90%' }}>
                                  <RavenFormikField
                                    errors={errors}
                                    touched={touched}
                                    name={"cardNumber"}
                                    label={"cardNumber"}
                                    type="text"
                                    maxLength={16}
                                    style={{ width: '100%', paddingRight: cardBrandSrc ? '50px' : undefined }}
                                    placeholder={this.props.t("cardNumber")}
                                    hideLabel={true}
                                    setFieldValue={setFieldValue}
                                    onChange={this.handleCardNoChange}
                                  />
                                  {cardBrandSrc ? (
                                    <img
                                      src={`data:image/png;base64,${cardBrandSrc}`}
                                      alt=""
                                      style={{
                                        width: '40px',
                                        height: 'auto',
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        zIndex: 2,
                                        pointerEvents: 'none',
                                      }}
                                    />
                                  ) : null}
                                </div>

                                {this.props.checkBinLoading && (
                                  <div style={{ width: '90%', marginTop: '0.5rem' }}>
                                    <Spinner size="sm" color="primary" />
                                  </div>
                                )}

                                {checkBinErrorMessage && !this.props.checkBinLoading && (
                                  <Alert color="danger" className="mt-2" style={{ width: '90%' }}>
                                    {checkBinErrorMessage}
                                  </Alert>
                                )}

                                {installmentErrorMessage && !this.props.getInstallmentsLoading && (
                                  <Alert color="danger" className="mt-2" style={{ width: '90%' }}>
                                    {installmentErrorMessage}
                                  </Alert>
                                )}

                                <div style={{ width: '90%', display: 'flex', gap: '0.5rem' }}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <RavenFormikField
                                      errors={errors}
                                      touched={touched}
                                      name={"expireDate"}
                                      label={"expireDate"}
                                      type="text"
                                      maxLength={7}
                                      style={{ width: '100%' }}
                                      setFieldValue={setFieldValue}
                                      placeholder={this.props.t("common.expireDate")?.replace("/", " / ")}
                                      hideLabel={true}
                                      onChange={(e, setFieldValueParam) => {
                                        const digits = getExpiryDigits(e.target.value)
                                        const formatted = formatExpiryForDisplay(digits)
                                        setFieldValueParam(objectNames.expireDate, formatted)
                                        this.props.onInitializePayData(
                                          this.props.payData,
                                          digits,
                                          objectNames.expireDate,
                                        )
                                      }}
                                    />
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <RavenFormikField
                                      errors={errors}
                                      touched={touched}
                                      name={"cvv"}
                                      label={"cvv"}
                                      type="text"
                                      maxLength={3}
                                      style={{ width: '100%' }}
                                      placeholder={this.props.t("cvv")}
                                      hideLabel={true}
                                      setFieldValue={setFieldValue}
                                      onChange={e => {
                                        this.handleChangeOperation(e, objectNames.cvv, "s");
                                      }}
                                    />
                                  </div>
                                </div>

                                <RavenFormikField
                                  errors={errors}
                                  touched={touched}
                                  name={"threeDPay"}
                                  label={"threeDPay"}
                                  htmlLabel={true}

                                  setFieldValue={setFieldValue}
                                  checked={this.state.threeDPay}
                                  onChange={e => {
                                    this.handleChangeOperation(e, objectNames.threeDPay, "ch");
                                  }}
                                  type="checkbox"
                                  disabled={this.props.recordMerchantPermission.threeDPaymentEnabled && !this.props.recordMerchantPermission.noneSecurePaymentEnabled}
                                />

                                <Container>
                                  {this.props.getInstallmentsLoading ?
                                    (
                                      <Spinner className="position-absolute top-50 start-50 primary" />
                                    )
                                    : (
                                      <Row className="justify-content-center" style={{ display: displayNoneInline(this.state.cardNumberValueCheck && this.props.recordList ? false : true) }}>
                                        <ListGroup style={{ borderColor: "gray" }}>
                                          <ListGroupItem className="justify-content-between" style={{ fontWeight: "bold", fontSize: "14px", borderColor: "gray" }}>
                                            <div className="checkout-installment-table-header">
                                              <span>{this.props.t("installmentChoose")}</span>
                                              <span>{this.props.t("totalAmount")}</span>
                                            </div>
                                          </ListGroupItem>
                                          {
                                            installmentList ?
                                              installmentList.map(element =>
                                                <ListGroupItem className="justify-content-between" style={{ fontWeight: "normal", fontSize: "12px", borderColor: "gray" }} key={uniqueIdentifier()}
                                                  href="#"
                                                  tag="a"
                                                >
                                                  <Row className="justify-content-center">
                                                    <Col xs={6} sm={6} md={6} lg={4} xl={4} className="align-items-center">
                                                      <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={element.installmentId}
                                                        onChange={e => {
                                                          this.handleChangeOperation(e, objectNames.installmentCount, "c", installmentList);
                                                        }}
                                                        checked={this.state.installmentCount === element.installmentId ? true : false}

                                                      />
                                                      <span style={{ marginLeft: "5px", whiteSpace: "nowrap" }}> {element.installmentId == "1" ? this.props.t("linkPaymentTekCekim") : element.installmentId + " " + this.props.t("linkPaymentTaksit")} </span>
                                                    </Col>
                                                    <Col style={{ textAlign: "right" }}>
                                                      {element.installmentId == "1" ? '\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0' : "(" + element.installmentAmountSimulation + ")"}{'\xa0'}
                                                      <span className="checkout-currency-amount" style={{ fontSize: "18px", fontWeight: "bold" }} dir="ltr">
                                                        {formatCheckoutCurrency(element.installmentTotalAmount, this.props.recordList?.y)}
                                                      </span>
                                                    </Col>
                                                  </Row>
                                                </ListGroupItem>
                                              ) : this.state.installmentList.map(element =>
                                                <ListGroupItem className="justify-content-between" style={{ fontWeight: "normal", fontSize: "12px", borderColor: "gray" }} key={uniqueIdentifier()}
                                                  href="#"
                                                  tag="a"
                                                >
                                                  <Row className="justify-content-center">
                                                    <Col>
                                                      <input
                                                        type="checkbox"
                                                        id={"1"}
                                                        className="form-check-input"
                                                        checked={true}
                                                        disabled={true}
                                                      />
                                                      <span style={{ marginLeft: "5px" }}> {element.installmentId == "1" ? this.props.t("linkPaymentTekCekim") : element.installmentId + " " + this.props.t("linkPaymentTaksit")} </span>
                                                    </Col>
                                                    <Col style={{ textAlign: "right" }}>
                                                      {element.installmentId == "1" ? '\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0' : "(" + element.installmentAmountSimulation + ")"}{'\xa0'}
                                                      <span className="checkout-currency-amount" style={{ fontSize: "18px", fontWeight: "bold" }} dir="ltr">
                                                        {formatCheckoutCurrency(element.installmentTotalAmount, this.props.recordList?.y)}
                                                      </span>
                                                    </Col>
                                                  </Row>
                                                </ListGroupItem>
                                              )
                                          }
                                        </ListGroup>
                                      </Row>)}
                                </Container>

                              </ContentCardInfo>
                            </AccordionContainer>

                            {/* KVKK sözleşmesi */}
                            {/* Kvkk Checkbox */}
                            <div className="form-check form-switch d-flex align-items-center mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="kvkkConsent"
                                checked={this.state.kvkkConsent}
                                onChange={() => this.setState(prevState => ({ kvkkConsent: !prevState.kvkkConsent }))}
                                style={{
                                  backgroundColor: this.state.kvkkConsent ? '#9c27b0' : '#ccc',
                                  borderColor: '#ccc',
                                  marginTop: '20px',
                                  width: '30px',
                                  height: '20px',
                                  //cursor: 'not-allowed',
                                  //pointerEvents: 'none', 
                                  opacity: 1,
                                }}
                              />
                              <label className="form-check-label ms-3" htmlFor="kvkkConsent" style={{ fontSize: '16px', color: '#6c757d', marginTop: '20px' }}>
                                <span
                                  role="button"
                                  tabIndex="0"
                                  style={{ textDecoration: 'underline', color: '#6c757d', cursor: 'pointer' }}
                                  onClick={() => this.setState({ showKvkkModal: true })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      this.setState({ showKvkkModal: true });
                                    }
                                  }}
                                >
                                  {this.props.t("linkPaymentKVKK")}
                                </span>
                              </label>
                            </div>

                            {/* Kvkk Modal */}
                            {this.state.showKvkkModal && (
                              <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                <div className="modal-dialog modal-lg" role="document">
                                  <div className="modal-content">
                                    <div className="modal-header">
                                      <h5 className="modal-title">KVKK Aydınlatma Metni</h5>
                                      <button type="button" className="btn-close" onClick={() => this.setState({ showKvkkModal: false })}></button>
                                    </div>
                                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                      <iframe
                                        src="https://prod.morpara.com/media/uljluhod/morpos_kvkk_acikriza-2.html"
                                        style={{ width: '100%', height: '60vh', border: 'none' }}
                                        title="KVKK Metni"
                                      ></iframe>
                                    </div>
                                    <div className="modal-footer">
                                      <button type="button" className="btn btn-primary" onClick={() => this.setState({ kvkkConsent: true, showKvkkModal: false })}>
                                        Okudum, Onaylıyorum
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}


                            {/* Gizlilik sözleşmesi */}
                            {/* Gizlilik Checkbox */}
                            <div className="form-check form-switch d-flex align-items-center mb-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id="privacyConsent"
                                checked={this.state.privacyConsent}
                                onChange={() => this.setState(prevState => ({ privacyConsent: !prevState.privacyConsent }))}
                                style={{
                                  backgroundColor: this.state.privacyConsent ? '#9c27b0' : '#ccc',
                                  borderColor: '#ccc',
                                  marginTop: '20px',
                                  width: '30px',
                                  height: '20px',
                                  //cursor: 'not-allowed',
                                  //pointerEvents: 'none', 
                                  opacity: 1,
                                }}
                              />
                              <label className="form-check-label ms-3" htmlFor="privacyConsent" style={{ fontSize: '16px', color: '#6c757d', marginTop: '20px' }}>
                                <span
                                  role="button"
                                  tabIndex="0"
                                  style={{ textDecoration: 'underline', color: '#6c757d', cursor: 'pointer' }}
                                  onClick={() => this.setState({ showPrivacyModal: true })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      this.setState({ showPrivacyModal: true });
                                    }
                                  }}
                                >
                                  {this.props.t("linkPaymentGizlilik")}
                                </span>
                              </label>
                            </div>

                            {/* Gizlilik Modal */}
                            {this.state.showPrivacyModal && (
                              <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                <div className="modal-dialog modal-lg" role="document">
                                  <div className="modal-content">
                                    <div className="modal-header">
                                      <h5 className="modal-title">Gizlilik Sözleşmesi Metni</h5>
                                      <button type="button" className="btn-close" onClick={() => this.setState({ showPrivacyModal: false })}></button>
                                    </div>
                                    <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                      <iframe
                                        src="https://prod.morpara.com/media/pmzjcvv2/morpos_gizlilik-2.html"
                                        style={{ width: '100%', height: '60vh', border: 'none' }}
                                        title="Gizlilik Sözleşmesi Metni"
                                      ></iframe>
                                    </div>
                                    <div className="modal-footer">
                                      <button type="button" className="btn btn-primary" onClick={() => this.setState({ privacyConsent: true, showPrivacyModal: false })}>
                                        Okudum, Onaylıyorum
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            <div
                              className="p-2"
                              style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}
                            >
                              <div style={{ display: "flex", alignItems: "center", marginTop: "10px", marginBottom: "10px" }}>
                                <img
                                  src={morPosLogo}
                                  alt="morPosLogo"
                                  style={{ width: '50px', height: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', marginRight: '10px' }}
                                />
                                < Label
                                  className="form-label"
                                  style={{
                                    marginTop: "10px",
                                    fontSize: "12px",
                                    textAlign: 'left',
                                    display: 'flex',
                                    justifyContent: 'left'
                                  }}
                                >
                                  {this.props.t("morPosGüvenliOde")}
                                </Label>
                              </div>

                              <button
                                className="btn btn-success btn-block d-flex align-items-center justify-content-between"
                                type="submit"
                                disabled={!(this.state.kvkkConsent && this.state.privacyConsent)}
                                style={{
                                  width: '300px',
                                  height: '50px',
                                  borderRadius: '30px',
                                  fontSize: '15px',
                                  padding: '0 40px',
                                  marginLeft: '20px',
                                }}
                              >
                                <span style={{ fontWeight: '500' }}>
                                  {this.props.t("completePayment")}
                                </span>

                                <span className="checkout-currency-amount" style={{ fontWeight: 'bold', fontSize: '17px' }} dir="ltr">
                                  {formatCheckoutCurrency(
                                    this.state.installmentCount === "1"
                                      ? this.props.recordList?.u
                                      : this.props.payData?.amount,
                                    this.props.recordList?.y,
                                  )}
                                </span>
                              </button>
                            </div>

                          </Col>

                        </Row>

                      </fieldset>
                    </Form>
                  )}
                </Formik>
              </CardBody>

              <div className="mt-5 text-center">
                <p><strong>© {new Date().getFullYear()} Morpara</strong></p>
              </div>
              <LoginFooter className="login-footer-container" text={this.props.t("login.footerText")} />
            </Card>
          </Row>

        </Container>

      </React.Fragment >
    );
  }
}

CardPaymentForm.propTypes = {
  onCloseClick: PropTypes.func,
  onGetInstallments: PropTypes.func,
  onGetCheckBinCardNumber: PropTypes.func,
  onInitializePayData: PropTypes.func,
  onPay: PropTypes.func,
  errorMessage: PropTypes.any,
  recordList: PropTypes.any,
  installmentRecord: PropTypes.any,
  installmentError: PropTypes.any,
  show: PropTypes.any,
  getInstallmentsLoading: PropTypes.bool,
  t: PropTypes.any,
  payData: PropTypes.any,
  result: PropTypes.any,
  resultLoading: PropTypes.PropTypes.bool,
  error: PropTypes.any,
  checkBinCardRecord: PropTypes.any,
  checkBinError: PropTypes.any,
  checkBinLoading: PropTypes.PropTypes.bool,
  onClearCheckBinError: PropTypes.func,
  onGetMerchantPermissionByMerchantId: PropTypes.func,
  recordMerchantPermission: PropTypes.any,
};
const mapStateToProps = ({
  installment, merchantLinkPayment, merchantPermission
}) => ({
  getInstallmentsLoading: installment.loading,
  installmentRecord: installment.recordList,
  payData: installment.payData,
  result: merchantLinkPayment.recordList,
  resultLoading: merchantLinkPayment.loading,
  checkBinCardRecord: merchantLinkPayment.checkBinCardRecord,
  checkBinError: merchantLinkPayment.checkBinError,
  checkBinLoading: merchantLinkPayment.checkBinLoading,
  installmentError: installment.error,
  recordMerchantPermission: merchantPermission.record,
});

const mapDispatchToProps = dispatch => ({
  onGetInstallments: record => dispatch(getInstallments(record)),
  onGetCheckBinCardNumber: record => dispatch(merchantLinkCheckBinCardNumber(record)),
  onClearCheckBinError: () => dispatch(clearMerchantLinkCheckBinError()),
  onPay: record => dispatch(merchantLinkDoPayment(record)),
  onInitializePayData: (record, value, name) => dispatch(initializePayData(record, value, name)),
  onApiError: error => dispatch(installmentApiError(error)),
  onGetMerchantPermissionByMerchantId: merchantId => dispatch(getMerchantPermissionByMerchantId(merchantId)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(CardPaymentForm));

